// 이용약관 v7.0 제4조의7 14항 — 기존 치과 회원의 재확인 판정 (2026-10-06 코드 준비, 시행 2026-11-06 — isTermsV70Effective 로 게이트)
//
// 14항: "본 조 개정 전에 가입한 회원에 대하여는 시행일부터 적용하되, 환자관리 서비스(제1항 나목부터 사목까지)는 회원이 해당 기능에 처음 진입할 때
//        본 조에 대한 동의를 다시 확인한 후 개방합니다. 동의하지 않는 회원은 환자관리 서비스만 이용하지 않으며, 그 밖의 서비스 이용에는 영향이 없습니다."
//
// 판정 자료 = users/{대표 uid}.consents.delegation.version (가입 시 Register.js 가 기록, 재확인 모달이 'v7.0' 으로 갱신).
//   - 대표(businessType 치과): 본인 users 문서.
//   - 직원(companyId + companyBusinessType 치과): 대표(companyId) users 문서를 1회 읽는다(rules users get — 업체 계정은 환자 아닌 users 열람 가능).
//     직원은 확인할 수 없고(대표에게 안내만) 대표가 확인하면 열린다.
//   - 시행 전(isTermsV70Effective false)·치과 아님·개인회원 → required false (아무 것도 하지 않음).
// 잠그는 범위(App.js DelegationV70Gate): /patients·/patients/:pid·/reception·/clinic-recalls·/settings/feedback·/settings/roadmap·/settings/walkin-qr
//   = 제4조의7 제1항 나~사목(환자관리·첫 방문·앱 등록·데스크 접수·재내원 안내·진료 후 질문). 그 밖의 기능은 그대로.
// 직원 문서 읽기 실패 = 잠금 유지(fail-closed) + console.error + [다시 확인] 재시도 — 조용히 열지 않는다.
import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isTermsV70Effective } from './features';
import { V70_DELEGATION_VERSION } from '../config/termsV70';

export const isClinicBusinessType = (bt) => ['clinic', 'dental', '치과'].includes(bt || '');

/** users 문서의 제4조의7 동의가 v7.0 인가 */
export function isDelegationV70Confirmed(userDoc) {
    return userDoc?.consents?.delegation?.version === V70_DELEGATION_VERSION;
}

/** 대표가 가진 구조 그대로 — 직원 여부·대표 uid */
export function delegationV70Subject(userData) {
    if (!userData) return { isStaff: false, isClinic: false, ownerUid: null };
    const isStaff = !!userData.companyId;
    const isClinic = isStaff ? isClinicBusinessType(userData.companyBusinessType) : isClinicBusinessType(userData.businessType);
    return { isStaff, isClinic, ownerUid: isStaff ? userData.companyId : (userData.uid || null) };
}

/**
 * @returns {{ required: boolean, isStaff: boolean, loading: boolean, error: string, reload: Function }}
 *   required = 시행 후 && 치과 업체 계정 && 대표 문서 delegation.version !== 'v7.0' (→ 환자관리 기능 잠금 + 모달)
 *   loading  = 직원이 대표 문서를 읽는 중(그동안 잠금 화면은 "확인 중…")
 */
export function useDelegationV70(features, userData) {
    const effective = isTermsV70Effective(features);
    const { isStaff, isClinic, ownerUid } = delegationV70Subject(userData);
    const ownerConfirmedSelf = !isStaff && isDelegationV70Confirmed(userData);
    const active = effective && isClinic && !!ownerUid;

    // 직원: 대표 문서 조회 결과 (null = 아직 모름)
    const [staffState, setStaffState] = useState({ ownerUid: null, confirmed: null, loading: false, error: '' });
    const [tick, setTick] = useState(0);
    const reload = useCallback(() => setTick(t => t + 1), []);

    useEffect(() => {
        if (!active || !isStaff) return undefined;
        let alive = true;
        setStaffState({ ownerUid, confirmed: null, loading: true, error: '' });
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', ownerUid));
                if (!alive) return;
                if (!snap.exists()) {
                    console.error('[약관 v7.0 재확인] 대표 문서 없음 — 잠금 유지:', ownerUid);
                    setStaffState({ ownerUid, confirmed: false, loading: false, error: '대표 계정 정보를 찾지 못했습니다.' });
                    return;
                }
                setStaffState({ ownerUid, confirmed: isDelegationV70Confirmed(snap.data()), loading: false, error: '' });
            } catch (e) {
                if (!alive) return;
                console.error('[약관 v7.0 재확인] 대표 문서 읽기 실패 — 잠금 유지:', e?.code || e?.message || e);
                setStaffState({ ownerUid, confirmed: false, loading: false, error: '대표 계정의 확인 상태를 불러오지 못했습니다.' });
            }
        })();
        return () => { alive = false; };
    }, [active, isStaff, ownerUid, tick]);

    if (!active) return { required: false, isStaff, loading: false, error: '', reload };
    if (!isStaff) return { required: !ownerConfirmedSelf, isStaff: false, loading: false, error: '', reload };
    const fresh = staffState.ownerUid === ownerUid;
    return {
        required: !(fresh && staffState.confirmed === true),
        isStaff: true,
        loading: !fresh || staffState.loading,
        error: fresh ? staffState.error : '',
        reload,
    };
}

/** 잠금 화면 → 모달 열기 (모달은 MainLayout 에 1개 — 라우트 쪽은 이벤트로 요청) */
export const DELEGATION_V70_OPEN_EVENT = 'dc:delegationV70:open';
export function requestDelegationV70Modal() {
    try { window.dispatchEvent(new CustomEvent(DELEGATION_V70_OPEN_EVENT)); }
    catch (e) { console.error('[약관 v7.0 재확인] 모달 열기 이벤트 실패:', e?.message || e); }
}
